/**
 * Invoicing, invoice batches, dashboards & approval requests.
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { extractItems } from '../../utils/apiHelpers';
/**
 * Send invoice to specified recipients
 * @param workOrderId - The work order ID
 * @param request - Send invoice request with recipient user IDs, external emails, and optional message
 */
export async function sendInvoice(workOrderId, request) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/send-invoice`, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to send invoice');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Send invoice error:', error);
        throw error;
    }
}
/**
 * Get closed work orders for dashboard invoice queue
 */
export async function getClosedWorkOrdersForDashboard(locationId, tagIds, sortBy, sortDesc) {
    try {
        const params = {};
        if (tagIds && tagIds.length > 0) {
            params.tag_ids = tagIds.join(',');
        }
        if (sortBy)
            params.sort_by = sortBy;
        if (sortDesc != null)
            params.sort_desc = sortDesc;
        const response = await this.client.get(`/locations/${locationId}/dashboard/closed-work-orders`, Object.keys(params).length > 0 ? params : undefined);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to get closed work orders');
        }
        const items = response.data.work_orders || response.data.line_items || [];
        return {
            work_orders: items,
            total: response.data.total || items.length,
        };
    }
    catch (error) {
        console.error('[WorkOrdersService] Get closed work orders for dashboard error:', error);
        throw error;
    }
}
/**
 * Send invoices from dashboard (location-scoped batch send)
 */
export async function sendDashboardInvoices(locationId, request) {
    try {
        const response = await this.client.post(`/locations/${locationId}/dashboard/send-invoices`, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to send invoices');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Send dashboard invoices error:', error);
        throw error;
    }
}
/**
 * Send invoices (bulk, non-location-scoped)
 */
export async function sendInvoices(request) {
    try {
        const response = await this.client.post(`/work-orders/send-invoices`, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to send invoices');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Send invoices error:', error);
        throw error;
    }
}
/**
 * List invoice batches for an account
 */
export async function listInvoiceBatches(accountId, options) {
    try {
        const params = {};
        if (options?.locationId)
            params.location_id = options.locationId;
        if (options?.locationIds && options.locationIds.length > 0) {
            params.location_ids = options.locationIds.join(',');
        }
        if (options?.tagIds && options.tagIds.length > 0)
            params.tag_ids = options.tagIds.join(',');
        if (options?.limit)
            params.limit = options.limit;
        if (options?.offset)
            params.offset = options.offset;
        if (options?.search)
            params.search = options.search;
        // begin_date / end_date are YYYY-MM-DD; the backend interprets them as
        // inclusive day bounds (in the account's timezone) against
        // invoice_batches.created_at.
        if (options?.beginDate)
            params.begin_date = options.beginDate;
        if (options?.endDate)
            params.end_date = options.endDate;
        if (options?.paymentStatuses && options.paymentStatuses.length > 0) {
            params.payment_status = options.paymentStatuses.join(',');
        }
        if (options?.syncStates && options.syncStates.length > 0) {
            params.accounting_sync_state = options.syncStates.join(',');
        }
        if (options?.sort) {
            params.sort = options.sort.column;
            params.dir = options.sort.dir;
        }
        const response = await this.client.get(`/accounts/${accountId}/invoice-batches`, Object.keys(params).length > 0 ? params : undefined);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to list invoice batches');
        }
        return {
            batches: response.data.batches || [],
            total: response.data.total || 0,
            limit: response.data.limit || options?.limit || 50,
            offset: response.data.offset || options?.offset || 0,
            invoicing_mode: response.data.invoicing_mode,
            can_see_details: response.data.can_see_details,
        };
    }
    catch (error) {
        console.error('[WorkOrdersService] List invoice batches error:', error);
        throw error;
    }
}
/**
 * Global top-bar search for invoice batches. Backed by
 * GET /invoice-batches/search, which pins the account scope to the caller's
 * JWT so the client doesn't need to supply accountId. Soft-fails to an empty
 * array — the global header runs this alongside searchWorkOrders and a
 * transient batch-search error shouldn't blank out WO results.
 */
export async function searchInvoiceBatches(query, limit = 8) {
    const q = (query || '').trim();
    if (!q)
        return [];
    try {
        const response = await this.client.get('/invoice-batches/search', { query: q, limit });
        if (!response.success || !response.data) {
            return [];
        }
        return Array.isArray(response.data.batches) ? response.data.batches : [];
    }
    catch (error) {
        console.error('[WorkOrdersService] Search invoice batches error:', error);
        return [];
    }
}
/**
 * Get invoice batch details
 */
export async function getInvoiceBatch(batchId) {
    try {
        const response = await this.client.get(`/invoice-batches/${batchId}`);
        if (!response.success || !response.data) {
            // Keep the status and body: OTHER_ACCOUNT names the store to switch to.
            throw Object.assign(new Error(response.error || 'Failed to get invoice batch'), {
                status: response.status,
                errorBody: response.errorBody,
            });
        }
        return response.data.batch || response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get invoice batch error:', error);
        throw error;
    }
}
/**
 * List activity audit rows scoped to a batch's parent_resource_id.
 * Used by the nested delivery history UI so one fetch covers every delivery under a batch.
 * Backend: GET /api/accounts/{accountId}/activities?parent_resource_id={batchId}&limit={limit}
 */
export async function listActivitiesByBatch(accountId, batchId, limit = 50) {
    try {
        const params = {
            parent_resource_id: batchId,
            limit,
        };
        const response = await this.client.get(`/accounts/${accountId}/activities`, params);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to list activities for batch');
        }
        return extractItems(response.data, ['items', 'activities', 'data']);
    }
    catch (error) {
        console.error('[WorkOrdersService] List activities by batch error:', error);
        throw error;
    }
}
/**
 * Resend a specific invoice delivery (creator, distributor, or prior resend).
 * Backend: POST /api/invoice-deliveries/{deliveryId}/resend
 */
export async function resendDelivery(deliveryId) {
    try {
        const response = await this.client.post(`/invoice-deliveries/${deliveryId}/resend`, {});
        if (!response.success) {
            throw new Error(response.error || 'Failed to resend delivery');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Resend delivery error:', error);
        throw error;
    }
}
/**
 * Resend an invoice batch, optionally overriding the recipient email
 */
export async function resendInvoiceBatch(batchId, overrideEmail) {
    try {
        const body = {};
        if (overrideEmail)
            body.override_email = overrideEmail;
        const response = await this.client.post(`/invoice-batches/${batchId}/resend`, body);
        if (!response.success) {
            throw new Error(response.error || 'Failed to resend invoice batch');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Resend invoice batch error:', error);
        throw error;
    }
}
/**
 * Get work orders awaiting approval for the dashboard approval queue
 */
export async function getAwaitingApprovalWorkOrders(locationId, tagIds) {
    try {
        let path = `/locations/${locationId}/dashboard/awaiting-approval`;
        if (tagIds && tagIds.length > 0) {
            path += `?tag_ids=${tagIds.join(',')}`;
        }
        const response = await this.client.get(path);
        if (!response.success) {
            throw new Error(response.error || 'Failed to get approval queue');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get approval queue error:', error);
        throw error;
    }
}
/**
 * Get users with approval permission at a location
 */
export async function getApprovalUsers(locationId) {
    try {
        const response = await this.client.get(`/locations/${locationId}/dashboard/approval-users`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to get approval users');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get approval users error:', error);
        throw error;
    }
}
/**
 * Send approval request emails/SMS for selected work orders
 */
export async function sendApprovalRequests(locationId, request) {
    try {
        const response = await this.client.post(`/locations/${locationId}/dashboard/send-approval-requests`, request);
        if (!response.success) {
            throw new Error(response.error || 'Failed to send approval requests');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Send approval requests error:', error);
        throw error;
    }
}
/**
 * Mark many work orders as Ignored ('exclude') or Pending ('include') in one
 * request — backs the invoice report's bulk "Mark as Ignored / Mark as
 * Pending" toolbar actions. Work orders already in a batch are skipped server
 * side. Returns how many were actually updated vs requested. Requires the
 * invoices:exclude permission server-side.
 */
export async function setWorkOrderInvoiceDispositionBulk(workOrderIds, action) {
    try {
        const response = await this.client.post(`/work-orders/invoice-disposition/bulk`, { work_order_ids: workOrderIds, action });
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to update invoice dispositions');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Set work order invoice disposition bulk error:', error);
        throw error;
    }
}
/**
 * Create a (pending, not-yet-sent) invoice batch from the given work orders —
 * the "Add to batch" bulk action on the invoice report. The work orders must
 * not already be in a batch. Recipients/sending are handled separately.
 */
export async function createInvoiceBatchFromWorkOrders(accountId, workOrderIds) {
    try {
        const response = await this.client.post(`/accounts/${accountId}/invoice-batches`, { work_order_ids: workOrderIds });
        if (!response.success || !response.data) {
            // The API returns {"error": "...", "code": "..."}; the client already
            // surfaces the human message in response.error.
            throw new Error(response.error || 'Failed to create invoice batch');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Create invoice batch from work orders error:', error);
        throw error;
    }
}
/**
 * Bulk-resend N invoice batches to a shared recipient set. Mirrors the
 * SendInvoiceModal payload used by the work-order invoice list so the
 * batches grid can reuse the same modal. The backend loops the batches
 * and creates one new delivery per batch with trigger_source='bulk_resend'.
 * The response includes per-batch outcomes so a partial failure (one
 * batch's email bounces) doesn't hide the rest.
 */
export async function bulkResendInvoiceBatches(request) {
    try {
        const response = await this.client.post(`/invoice-batches/bulk-resend`, request);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to bulk-resend batches');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Bulk resend invoice batches error:', error);
        throw error;
    }
}
export async function getInvoiceSentTo(batchId) {
    const response = await this.client.get(`/invoice-batches/${batchId}/sent-to`);
    if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load who the invoice went to');
    }
    return {
        mode: response.data.invoicing_mode ?? 'classic',
        canSeeDetails: !!response.data.can_see_details,
        contacts: response.data.contacts ?? {},
    };
}
